import React, { useState, useEffect } from 'react';
import { ClothingItem } from '../../services/clothing';
import { Input } from './Input';
import { Button } from './Button';
import { getImageUrl } from '../../utils/imageUrl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';

interface EditClothingModalProps {
  isOpen: boolean;
  item: ClothingItem | null;
  onClose: () => void;
  onSave: (id: string, data: {
    category: string;
    subcategory?: string;
    color?: string;
    brand?: string;
    notes?: string;
  }) => Promise<void>;
}

export const EditClothingModal: React.FC<EditClothingModalProps> = ({
  isOpen,
  item,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    category: '',
    subcategory: '',
    color: '',
    brand: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (item) {
      setFormData({
        category: item.category || '',
        subcategory: item.subcategory || '',
        color: item.color || '',
        brand: item.brand || '',
        notes: item.notes || '',
      });
      setError(null);
    }
  }, [item]);

  if (!item) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(item.id, formData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Clothing Item</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
            <div className="aspect-square max-w-md mx-auto rounded-lg overflow-hidden shadow-md">
                <img
                  src={getImageUrl(item.image_url)}
                  alt={item.category}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                <Label>Category *</Label>
                  <Input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    required
                    placeholder="e.g., shirt, pants, dress"
                  />
                </div>

                <div>
                <Label>Subcategory</Label>
                  <Input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => handleChange('subcategory', e.target.value)}
                    placeholder="e.g., t-shirt, jeans, sneakers"
                  />
                </div>

                <div>
                <Label>Color</Label>
                  <Input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleChange('color', e.target.value)}
                    placeholder="e.g., blue, red, black"
                  />
                </div>

                <div>
                <Label>Brand</Label>
                  <Input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => handleChange('brand', e.target.value)}
                    placeholder="e.g., Nike, Zara"
                  />
                </div>

                <div>
                <Label>Notes</Label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any additional notes..."
                    rows={3}
                  className={cn(
                    "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                    "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  )}
                  />
                </div>

                {error && (
                <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
                    {error}
                  </div>
                )}

              <DialogFooter className="gap-2">
                  <Button
                    type="button"
                  variant="outline"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
              </DialogFooter>
              </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

